import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BriefcaseBusiness, KeyRound, Stethoscope, UserRound } from 'lucide-react';
import { get, post, put, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateTime, humanize } from '@/lib/utils';
import { formatMoneyInput, minorFromInput } from '@/components/MoneyInput';
import { OTHER_STAFF_KIND, STAFF_KINDS, assignmentBasis, countryProfile, staffFieldsFor, staffKindForDesignation, supportedCountryOrNull, term } from '@his/shared';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Badge, Button, DatePicker, Field, Input, Select, Switch } from '@/components/ui/primitives';
import { PhoneText } from '@/components/ui/phone-text';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { useWorkingBranchId } from '@/lib/branch';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { generatePassword, holdsLicensedRole, licenceRequired } from './licence';
import { DepartmentsField } from './DepartmentsField';
import {
  branchToSend,
  departmentChoices,
  departmentIdsOfPerson,
  doctorLacksLogin,
  initialLogin,
  loginAfterDesignation,
  personBranchId,
} from './person-form';
import { UnlockButton, WebSignInSwitch } from './LoginActions';
import { UserControlsMenu } from './UserControls';
import { assignmentPayload, assignmentRowsOf, RoleAssignmentsField, useBranchScope, type AssignmentRow } from './RoleAssignments';
import { SignatureField } from './SignatureField';
import { PEOPLE_QUERY_KEY, PERSON_QUERY_KEY, type EmployeeRecord, type PersonDetail, type Role } from './types';

const CONTRACT_TYPES = ['permanent', 'contract', 'visiting', 'intern', 'daily_wage'];

interface PersonForm {
  name: string;
  nameLocal: string;
  email: string;
  phone: string;
  designation: string;
  departmentIds: string[];
  login: boolean;
  username: string;
  password: string;
  assignments: AssignmentRow[];
  isDoctor: boolean;
  acceptsAppointments: boolean;
  specialtyId: string;
  registrationNo: string;
  registrationBody: string;
  employment: boolean;
  employeeNo: string;
  cnic: string;
  contractType: string;
  joinedAt: string;
  contractEndsAt: string;
  licenseNo: string;
  licenseExpiry: string;
  basicSalary: string;
  bankAccount: string;
}

const dateField = (value: string | null | undefined) => (value ? value.slice(0, 10) : '');

function employmentFieldsOf(employee: EmployeeRecord | null) {
  return {
    employment: Boolean(employee),
    employeeNo: employee?.employeeNo ?? '',
    cnic: employee?.cnic ?? '',
    contractType: employee?.contractType ?? 'permanent',
    joinedAt: dateField(employee?.joinedAt),
    contractEndsAt: dateField(employee?.contractEndsAt),
    licenseNo: employee?.licenseNo ?? '',
    licenseExpiry: dateField(employee?.licenseExpiry),
    basicSalary: formatMoneyInput(employee?.basicSalaryMinor ?? 0),
    bankAccount: employee?.bankAccount ?? '',
  };
}

function blankForm(): PersonForm {
  return {
    name: '',
    nameLocal: '',
    email: '',
    phone: '',
    designation: '',
    departmentIds: [],
    login: initialLogin(false),
    username: '',
    password: generatePassword(),
    assignments: [],
    isDoctor: false,
    acceptsAppointments: false,
    specialtyId: '',
    registrationNo: '',
    registrationBody: '',
    ...employmentFieldsOf(null),
  };
}

function formOf(detail: PersonDetail): PersonForm {
  const { user, employee } = detail;
  return {
    name: user?.name ?? employee?.name ?? '',
    nameLocal: user?.nameLocal ?? '',
    email: user?.email ?? employee?.email ?? '',
    phone: user?.phone ?? employee?.phone ?? '',
    designation: user?.designation ?? employee?.designation ?? '',
    departmentIds: departmentIdsOfPerson(user, employee),
    login: initialLogin(Boolean(user)),
    username: user?.username ?? '',
    password: user ? '' : generatePassword(),
    assignments: assignmentRowsOf(user?.roles ?? []),
    isDoctor: user?.isDoctor ?? false,
    acceptsAppointments: user?.acceptsAppointments ?? false,
    specialtyId: user?.specialty?.id ?? '',
    registrationNo: user?.registrationNo ?? '',
    registrationBody: user?.registrationBody ?? '',
    ...employmentFieldsOf(employee),
  };
}

export function PersonPage() {
  const { personKey = '' } = useParams();
  const [params] = useSearchParams();
  const { can } = useAuth();
  const manageUsers = can('user.manage');
  const showRoles = can('user.read', 'role.read');
  const returnTo = safeReturnTo(params.get('returnTo'), '/people');

  const detail = useQuery({
    queryKey: [...PERSON_QUERY_KEY, personKey],
    queryFn: () => get<PersonDetail>(`/people/${personKey}`),
    enabled: Boolean(personKey),
  });
  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: () => get<Role[]>('/org/roles'),
    enabled: showRoles || manageUsers,
  });

  const personName = detail.data?.user?.name ?? detail.data?.employee?.name ?? '';
  const title = personKey ? tr('admin', 'edit_x', { name: personName }) : tr('admin', 'add_a_person');
  const description = personKey
    ? tr('admin', 'changes_take_effect_the_next_time_they_load_a')
    : tr('admin', 'everyone_who_works_here_with_or_without_a_login');

  if (personKey && !detail.data) {
    return (
      <FormScreen title={title} description={description} icon={UserRound} backTo={returnTo} wide>
        <RecordState error={detail.error} label={tr('admin', 'loading_this_person')} />
      </FormScreen>
    );
  }

  return (
    <PersonForm
      key={personKey || 'new'}
      personKey={personKey || null}
      detail={detail.data ?? null}
      refetchDetail={async () => (await detail.refetch()).data ?? null}
      roles={roles.data ?? []}
      title={title}
      description={description}
      returnTo={returnTo}
    />
  );
}

function PersonForm({
  personKey,
  detail,
  refetchDetail,
  roles,
  title,
  description,
  returnTo,
}: {
  personKey: string | null;
  detail: PersonDetail | null;
  refetchDetail: () => Promise<PersonDetail | null>;
  roles: Role[];
  title: string;
  description: string;
  returnTo: string;
}) {
  const navigate = useNavigate();
  const { can, canWrite, hasModule, user: session } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const manageUsers = can('user.manage') && canWrite;
  const readUsers = can('user.read', 'user.manage');
  const readEmployees = hasModule('hr') && can('hr.employee.read', 'hr.employee.manage');
  const manageEmployees = hasModule('hr') && can('hr.employee.manage') && canWrite;
  const onlineCopy = session?.readOnlyReason === 'cloud_mirror';
  const staffFields = new Set(staffFieldsFor(session?.modules ?? []));

  const user = detail?.user ?? null;
  const [employee, setEmployee] = useState<EmployeeRecord | null>(detail?.employee ?? null);

  const [initial] = useState<PersonForm>(() => (detail ? formOf(detail) : blankForm()));
  const [form, setForm] = useState<PersonForm>(initial);
  const [error, setError] = useState<string | null>(null);
  const [linkEmployeeId, setLinkEmployeeId] = useState('');

  const editingSelf = Boolean(user && session && user.id === session.id);
  const canEditIdentity = personKey ? (user ? manageUsers : manageEmployees) : manageUsers || manageEmployees;
  const hasLogin = Boolean(user) || form.login;
  const chosenRoleCodes = form.assignments
    .map((row) => roles.find((role) => role.id === row.roleId)?.code)
    .filter((code): code is string => Boolean(code));
  const [otherKind, setOtherKind] = useState(false);
  const staffKind = otherKind ? OTHER_STAFF_KIND : staffKindForDesignation(form.designation, form.isDoctor);
  const showDoctorBlock = staffFields.has('isDoctor') || form.isDoctor;
  const showBookable = staffFields.has('acceptsAppointments') || form.acceptsAppointments;
  const showSpecialty = staffFields.has('specialty') && form.isDoctor;
  const showLicence = form.isDoctor || holdsLicensedRole(chosenRoleCodes) || Boolean(form.registrationNo);
  const showClinical = hasLogin && (showDoctorBlock || showBookable || showLicence);
  const workingBranchId = useWorkingBranchId();
  const placedBranchId = personBranchId(personKey, workingBranchId, user, employee);
  const departmentBranchId = placedBranchId || workingBranchId;
  const staffCountry = supportedCountryOrNull(session?.branches.find((branch) => branch.id === departmentBranchId)?.country ?? session?.branches[0]?.country ?? null);
  const localSecondary = countryProfile(staffCountry).locales.documentSecondary;
  const localLanguage = localSecondary === 'ar' ? 'Arabic' : localSecondary;
  const registrationCouncil = term('registration_council', 'en', staffCountry);
  const needsLicence = hasLogin && licenceRequired(form.isDoctor, chosenRoleCodes, hasModule('wasfaty'));

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () => get<{ id: string; name: string }[]>('/org/branches'),
  });
  const departments = useQuery({
    queryKey: ['departments'],
    queryFn: () => get<{ id: string; branchId: string; name: string }[]>('/org/departments'),
    enabled: can('department.read'),
  });
  const specialties = useQuery({
    queryKey: ['specialties'],
    queryFn: () => get<{ id: string; name: string; isActive: boolean }[]>('/org/specialties'),
    enabled: showSpecialty,
  });
  const unlinkedEmployees = useQuery({
    queryKey: ['employees', 'unlinked'],
    queryFn: () => get<EmployeeRecord[]>(`/hr/employees${qs({ unlinked: true, limit: 200 })}`),
    enabled: Boolean(user) && !employee && manageEmployees,
  });
  const branchList = branches.data ?? [];
  const { actorHospitalWide, allowedBranchIds } = useBranchScope(branchList);
  const departmentOptions = departmentChoices(departments.data ?? [], departmentBranchId, actorHospitalWide, allowedBranchIds);
  const bookableWithoutDepartment = form.acceptsAppointments && departments.isSuccess && form.departmentIds.length === 0;
  const placedBranchName = branchList.find((branch) => branch.id === placedBranchId)?.name ?? session?.branches.find((branch) => branch.id === placedBranchId)?.name ?? '';

  const refreshPeople = () => {
    void queryClient.invalidateQueries({ queryKey: PEOPLE_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: ['users'] });
    void queryClient.invalidateQueries({ queryKey: ['employees'] });
  };

  const link = useMutation({
    mutationFn: () => put(`/hr/employees/${linkEmployeeId}`, { userId: user?.id }),
    onSuccess: async () => {
      toast.success(tr('admin', 'hr_record_linked'));
      refreshPeople();
      const fresh = await refetchDetail();
      if (fresh?.employee) {
        setEmployee(fresh.employee);
        setForm((current) => ({ ...current, ...employmentFieldsOf(fresh.employee) }));
      }
    },
    onError: (caught) => toast.error(tr('admin', 'could_not_link_the_hr_record'), describeError(caught)),
  });

  const save = useMutation({
    mutationFn: async () => {
      const blank = personKey ? null : undefined;
      const text = (value: string) => value.trim() || blank;
      const dateAt = (value: string) => (value ? new Date(`${value}T00:00:00.000Z`).toISOString() : null);
      const assignments = form.assignments.filter((row) => row.roleId).map(assignmentPayload);
      const identity = {
        name: form.name.trim(),
        email: text(form.email),
        phone: text(form.phone),
        designation: text(form.designation),
        ...(localLanguage ? { nameLocal: text(form.nameLocal) } : {}),
        branchId: branchToSend(personKey, workingBranchId),
        departmentIds: form.departmentIds,
      };
      const clinical = {
        isDoctor: form.isDoctor,
        acceptsAppointments: form.acceptsAppointments,
        ...(staffFields.has('specialty') ? { specialtyId: form.isDoctor ? form.specialtyId || null : null } : {}),
        registrationNo: text(form.registrationNo),
        registrationBody: text(form.registrationBody),
      };
      const employment = {
        ...(form.employeeNo.trim() ? { employeeNo: form.employeeNo.trim() } : {}),
        cnic: text(form.cnic),
        contractType: form.contractType,
        joinedAt: dateAt(form.joinedAt),
        contractEndsAt: dateAt(form.contractEndsAt),
        licenseNo: text(form.licenseNo),
        licenseExpiry: dateAt(form.licenseExpiry),
        basicSalaryMinor: minorFromInput(form.basicSalary),
        bankAccount: text(form.bankAccount),
      };
      const newLogin = { username: form.username.trim(), password: form.password, roles: assignments };
      if (!personKey) {
        return post('/people', {
          identity,
          ...(form.login && manageUsers ? { login: newLogin, clinical } : {}),
          ...(form.employment && manageEmployees ? { employment } : {}),
        });
      }
      const createLogin = !user && form.login && manageUsers;
      await put(`/people/${personKey}`, {
        ...(canEditIdentity ? { identity } : {}),
        ...(createLogin ? { login: newLogin } : {}),
        ...((user || createLogin) && manageUsers ? { clinical } : {}),
        ...(form.employment && manageEmployees ? { employment } : {}),
      });
      const held = (user?.roles ?? []).map((role) => ({ roleId: role.id, branchId: role.branchId, expiresAt: role.expiresAt }));
      if (user && manageUsers && !editingSelf && assignmentBasis(assignments) !== assignmentBasis(held)) {
        await put(`/org/users/${user.id}/roles`, { assignments, basis: user.assignmentBasis });
      }
      return null;
    },
    onSuccess: () => {
      const createdLogin = form.login && !user && manageUsers;
      toast.success(
        personKey ? tr('admin', 'person_updated') : tr('admin', 'person_created'),
        createdLogin ? tr('admin', 'they_must_change_the_password_at_first_sign_in') : undefined,
      );
      refreshPeople();
      void queryClient.invalidateQueries({ queryKey: PERSON_QUERY_KEY });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const missing: string[] = [];
  if (!form.name.trim()) missing.push('a full name');
  if (!personKey && !form.login && !form.employment) missing.push(tr('admin', 'a_login_or_an_hr_record'));
  if (doctorLacksLogin(form.isDoctor, hasLogin, manageUsers)) missing.push(tr('admin', 'a_doctor_needs_a_login_to_be_booked'));
  if (!user && form.login && !form.username.trim()) missing.push('a username to sign in with');
  if (!user && form.login && form.password.length < 10) {
    missing.push(`a temporary password of at least 10 characters (currently ${form.password.length})`);
  }
  if (needsLicence && !form.registrationNo.trim()) missing.push('a registration / licence number');
  if (form.assignments.some((row) => row.roleId && row.temporary && !row.until)) missing.push('an end date for each temporary role');
  if (bookableWithoutDepartment) missing.push(tr('admin', 'a_department_for_someone_who_takes_appointments'));
  if (!actorHospitalWide && form.assignments.some((row) => row.roleId && !row.branchId)) {
    missing.push('a branch for each role');
  }

  const dirty = !save.isSuccess && JSON.stringify(form) !== JSON.stringify(initial);

  return (
    <FormScreen
      title={title}
      description={description}
      icon={UserRound}
      backTo={returnTo}
      dirty={dirty}
      wide
      onSubmit={(event) => {
        event.preventDefault();
        if (missing.length > 0) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('admin', 'identity')} icon={UserRound}>
        <Field label={tr('admin', 'full_name')} required className="sm:col-span-2">
          <Input value={form.name} disabled={!canEditIdentity} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label={tr('admin', 'email')}>
          <Input type="email" value={form.email} disabled={!canEditIdentity} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </Field>
        <Field label={tr('admin', 'phone')}>
          <PhoneText value={form.phone} disabled={!canEditIdentity} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </Field>
        <Field label={tr('admin', 'designation')} hint={tr('admin', 'designation_hint')}>
          <Select
            value={staffKind}
            disabled={!canEditIdentity}
            onChange={(e) => {
              const chosen = STAFF_KINDS.find((kind) => kind.value === e.target.value);
              setOtherKind(e.target.value === OTHER_STAFF_KIND);
              setForm({
                ...form,
                login: loginAfterDesignation(form.login, Boolean(chosen?.isDoctor), manageUsers && !user),
                designation: chosen ? chosen.label : '',
                isDoctor: Boolean(chosen?.isDoctor),
                acceptsAppointments: chosen?.isDoctor ? true : form.isDoctor ? false : form.acceptsAppointments,
              });
            }}
          >
            <option value="">{tr('admin', 'choose')}</option>
            {STAFF_KINDS.map((kind) => (
              <option key={kind.value} value={kind.value}>
                {kind.label}
              </option>
            ))}
            <option value={OTHER_STAFF_KIND}>{tr('admin', 'designation_other')}</option>
          </Select>
        </Field>
        {staffKind === OTHER_STAFF_KIND && (
          <Field label={tr('admin', 'designation')}>
            <Input value={form.designation} disabled={!canEditIdentity} onChange={(e) => setForm({ ...form, designation: e.target.value })} />
          </Field>
        )}
        {localLanguage && (
          <Field label={tr('admin', 'name_x', { localLanguage: localLanguage })} hint={tr('admin', 'printed_beside_the_english_name_on_bilingual')}>
            <Input dir="auto" value={form.nameLocal} disabled={!canEditIdentity} onChange={(e) => setForm({ ...form, nameLocal: e.target.value })} />
          </Field>
        )}
        {placedBranchName && (
          <Field label={tr('admin', 'branch')} hint={personKey ? undefined : tr('admin', 'new_people_join_the_branch_you_work_in')}>
            <p className="flex h-10 items-center text-sm font-medium">{tr('admin', 'person_branch_x', { name: placedBranchName })}</p>
          </Field>
        )}
        {(departments.data?.length ?? 0) > 0 && (
          <Field
            label={tr('admin', 'departments')}
            required={form.acceptsAppointments}
            hint={form.acceptsAppointments ? tr('admin', 'needed_to_set_clinic_hours') : tr('admin', 'departments_of_this_branch_hint')}
          >
            <DepartmentsField
              options={departmentOptions}
              value={form.departmentIds}
              disabled={!canEditIdentity}
              onChange={(departmentIds) => setForm({ ...form, departmentIds })}
            />
            <input type="hidden" data-field-value value={form.departmentIds.join(',')} />
          </Field>
        )}
        {bookableWithoutDepartment && (departments.data?.length ?? 0) === 0 && (
          <div className="sm:col-span-2">
            <Alert variant="warning">{tr('admin', 'add_a_department_to_this_branch_first')}</Alert>
          </div>
        )}
      </FormSection>

      {(readUsers || manageUsers) && (
        <FormSection title={tr('admin', 'login')} icon={KeyRound}>
          {user ? (
            <>
              <Field label={tr('admin', 'username')}>
                <Input value={user.username} disabled readOnly />
              </Field>
              <div className="flex flex-wrap items-center gap-2 self-end pb-2">
                <Badge variant={user.is2faEnabled ? 'success' : 'muted'}>
                  {tr('admin', '2fa')} {user.is2faEnabled ? tr('admin', 'on') : tr('admin', 'off')}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {tr('admin', 'last_sign_in')}: {user.lastLoginAt ? dateTime(user.lastLoginAt) : tr('admin', 'never')}
                </span>
                {!user.isActive && <Badge variant="destructive">{tr('admin', 'inactive')}</Badge>}
                {user.lockedUntil && <Badge variant="warning">{tr('admin', 'locked_out')}</Badge>}
                {user.lockedUntil && manageUsers && <UnlockButton user={user} />}
                {onlineCopy && manageUsers && (
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    {tr('admin', 'web_sign_in')} <WebSignInSwitch user={user} />
                  </span>
                )}
                {!editingSelf && (
                  <UserControlsMenu
                    user={user}
                    trigger={
                      <Button type="button" variant="secondary" size="sm">
                        {tr('admin', 'account_actions')}
                      </Button>
                    }
                  />
                )}
              </div>
            </>
          ) : manageUsers ? (
            <div className="sm:col-span-2">
              <Switch
                checked={form.login}
                onCheckedChange={(checked) => setForm({ ...form, login: checked })}
                label={personKey ? tr('admin', 'create_login') : tr('admin', 'this_person_signs_in')}
                description={tr('admin', 'a_login_lets_them_open_the_system_leave_it_off')}
              />
            </div>
          ) : (
            <p className="text-xs text-muted-foreground sm:col-span-2">{tr('admin', 'none')}</p>
          )}
          {!user && form.login && manageUsers && (
            <>
              <Field label={tr('admin', 'username')} required hint={tr('admin', 'what_they_type_to_sign_in')}>
                <Input
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, '') })}
                />
              </Field>
              <Field
                label={tr('admin', 'temporary_password')}
                required
                hint={tr('admin', 'generated_for_you_they_change_it_at_first_sign')}
                error={form.password.length > 0 && form.password.length < 10 ? `At least 10 characters (currently ${form.password.length})` : undefined}
              >
                <div className="flex gap-2">
                  <Input type="text" value={form.password} readOnly />
                  <Button type="button" variant="secondary" onClick={() => setForm({ ...form, password: generatePassword() })}>
                    {tr('admin', 'generate')}
                  </Button>
                </div>
              </Field>
            </>
          )}
          {hasLogin && (manageUsers || readUsers) && (
            <RoleAssignmentsField
              rows={form.assignments}
              onChange={(assignments) => setForm({ ...form, assignments })}
              roles={roles}
              branches={branchList}
              editingSelf={editingSelf || !manageUsers}
            />
          )}
          {user && can('audit.read') && (
            <p className="text-sm text-muted-foreground sm:col-span-2">
              <Link className="text-primary underline-offset-4 hover:underline" to={`/admin/audit?entityType=users&entityId=${user.id}`}>
                {tr('admin', 'history_of_this_account')}
              </Link>
            </p>
          )}
          {hasLogin && can('authority.read', 'authority.manage') && (
            <p className="text-sm text-muted-foreground sm:col-span-2">
              {tr('admin', 'money_authority_is_set_in_accounts')}{' '}
              <Link className="text-primary underline-offset-4 hover:underline" to="/accounts/authority">
                {tr('admin', 'financial_authority')}
              </Link>
            </p>
          )}
        </FormSection>
      )}

      {showClinical && (
        <FormSection title={tr('admin', 'clinical')} icon={Stethoscope}>
          {showBookable && (
            <div className="sm:col-span-2">
              <Switch
                checked={form.acceptsAppointments}
                disabled={!manageUsers}
                onCheckedChange={(checked) => setForm({ ...form, acceptsAppointments: checked })}
                label={tr('admin', 'can_be_booked_for_appointments')}
                description={tr('admin', 'shown_in_the_booking_screens_and_the_calendar')}
              />
            </div>
          )}
          {showSpecialty && (
            <Field label={tr('admin', 'specialty')} hint={tr('admin', 'printed_under_the_doctors_name_add_more_under')}>
              <Select value={form.specialtyId} disabled={!manageUsers} onChange={(e) => setForm({ ...form, specialtyId: e.target.value })}>
                <option value="">—</option>
                {(specialties.data ?? [])
                  .filter((specialty) => specialty.isActive || specialty.id === form.specialtyId)
                  .map((specialty) => (
                    <option key={specialty.id} value={specialty.id}>
                      {specialty.name}
                    </option>
                  ))}
              </Select>
            </Field>
          )}
          {showLicence && (
            <Field label={tr('admin', 'registration_licence_no')} required={needsLicence} hint={tr('admin', 'printed_on_prescriptions_certificates_and')}>
              <Input value={form.registrationNo} disabled={!manageUsers} onChange={(e) => setForm({ ...form, registrationNo: e.target.value })} />
            </Field>
          )}
          {showLicence && (
            <Field label={tr('admin', 'registered_with')} hint={tr('admin', 'the_council_or_authority_that_issued_the_number')}>
              <Input
                value={form.registrationBody}
                disabled={!manageUsers}
                placeholder={registrationCouncil}
                onChange={(e) => setForm({ ...form, registrationBody: e.target.value })}
              />
            </Field>
          )}
          {user && staffFields.has('signature') && (showLicence || form.isDoctor) && (
            <div className="sm:col-span-2">
              <SignatureField userId={user.id} name={user.name} hasSignature={Boolean(user.hasSignature)} />
            </div>
          )}
        </FormSection>
      )}

      {readEmployees && (
        <FormSection title={tr('admin', 'employment')} description={tr('admin', 'the_hr_record_is_separate_from_the_login')} icon={BriefcaseBusiness}>
          <div className="sm:col-span-2">
            <Switch
              checked={form.employment}
              disabled={!manageEmployees || Boolean(employee)}
              onCheckedChange={(checked) => setForm({ ...form, employment: checked })}
              label={tr('admin', 'has_an_hr_record')}
              description={employee ? `${tr('admin', 'employee_no')} ${employee.employeeNo}` : tr('admin', 'contract_pay_registration_expiry_rosters_and_leave')}
            />
          </div>
          {user && !employee && !form.employment && manageEmployees && (
            <Field label={tr('admin', 'link_an_existing_hr_record')} className="sm:col-span-2">
              <div className="flex flex-wrap gap-2">
                <Select className="min-w-[14rem] flex-1" value={linkEmployeeId} onChange={(e) => setLinkEmployeeId(e.target.value)}>
                  <option value="">{tr('admin', 'choose_an_hr_record')}</option>
                  {(unlinkedEmployees.data ?? []).map((candidate) => (
                    <option key={candidate.id} value={candidate.id}>
                      {candidate.employeeNo} · {candidate.name}
                      {candidate.designation ? ` · ${candidate.designation}` : ''}
                    </option>
                  ))}
                </Select>
                <Button type="button" variant="secondary" disabled={!linkEmployeeId} loading={link.isPending} onClick={() => link.mutate()}>
                  {tr('admin', 'link')}
                </Button>
              </div>
              {unlinkedEmployees.data?.length === 0 && (
                <p className="mt-1 text-xs text-muted-foreground">{tr('admin', 'no_unlinked_hr_records')}</p>
              )}
            </Field>
          )}
          {form.employment && (
            <>
              {!employee && (
                <Field label={tr('admin', 'employee_no')} hint={tr('admin', 'left_empty_the_next_number_in_the_series_is_used')}>
                  <Input value={form.employeeNo} disabled={!manageEmployees} onChange={(e) => setForm({ ...form, employeeNo: e.target.value.toUpperCase() })} />
                </Field>
              )}
              <Field label={tr('admin', 'contract')}>
                <ChoiceButtons
                  layout="chips"
                  value={form.contractType}
                  disabled={!manageEmployees}
                  onChange={(value) => setForm({ ...form, contractType: value })}
                  options={CONTRACT_TYPES.map((type) => ({ value: type, label: humanize(type) }))}
                />
              </Field>
              <Field label={tr('admin', 'joined_on')}>
                <DatePicker value={form.joinedAt} disabled={!manageEmployees} onChange={(value) => setForm({ ...form, joinedAt: value })} />
              </Field>
              <Field label={tr('admin', 'contract_ends')}>
                <DatePicker value={form.contractEndsAt} disabled={!manageEmployees} onChange={(value) => setForm({ ...form, contractEndsAt: value })} />
              </Field>
              <Field label={tr('admin', 'national_id')}>
                <Input value={form.cnic} disabled={!manageEmployees} onChange={(e) => setForm({ ...form, cnic: e.target.value })} />
              </Field>
              <Field label={tr('admin', 'basic_salary')}>
                <Input
                  type="text"
                  dir="ltr"
                  inputMode="decimal"
                  value={form.basicSalary}
                  disabled={!manageEmployees}
                  onChange={(e) => setForm({ ...form, basicSalary: e.target.value })}
                />
              </Field>
              <Field label={tr('admin', 'registration_number')} hint={tr('admin', 'pmdc_nursing_council')}>
                <Input value={form.licenseNo} disabled={!manageEmployees} onChange={(e) => setForm({ ...form, licenseNo: e.target.value })} />
              </Field>
              <Field label={tr('admin', 'registration_expiry')}>
                <DatePicker value={form.licenseExpiry} disabled={!manageEmployees} onChange={(value) => setForm({ ...form, licenseExpiry: value })} />
              </Field>
              <Field label={tr('admin', 'bank_account')} className="sm:col-span-2">
                <Input value={form.bankAccount} disabled={!manageEmployees} onChange={(e) => setForm({ ...form, bankAccount: e.target.value })} />
              </Field>
            </>
          )}
        </FormSection>
      )}

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions
        submitLabel={personKey ? tr('admin', 'save_changes') : tr('admin', 'add_person')}
        loading={save.isPending}
        incomplete={missing.length > 0}
        cancelTo={returnTo}
        note={missing.length > 0 ? `${tr('admin', 'still_needed')} ${missing.join(', ')}` : undefined}
      />
    </FormScreen>
  );
}
